'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { styled, css } from '@mui/system';
import { Portal } from '@mui/base/Portal';
import { FocusTrap } from '@mui/base/FocusTrap';
import Fade from '@mui/material/Fade';
import { unstable_useModal as useModal } from '@mui/base/unstable_useModal';
export const Modal = React.forwardRef(function Modal(props, forwardedRef) {
    const {
      children,
      closeAfterTransition = false,
      container,
      disableAutoFocus = false,
      disableEnforceFocus = false,
      disableEscapeKeyDown = false,
      disablePortal = false,
      disableRestoreFocus = false,
      disableScrollLock = false,
      hideBackdrop = false,
      keepMounted = false,
      onClose,
      open,
      onTransitionEnter,
      onTransitionExited,
      ...other
    } = props;
  
    const propsWithDefaults = {
      ...props,
      closeAfterTransition,
      disableAutoFocus,
      disableEnforceFocus,
      disableEscapeKeyDown,
      disablePortal,
      disableRestoreFocus,
      disableScrollLock,
      hideBackdrop,
      keepMounted,
    };
  
    const {
      getRootProps,
      getBackdropProps,
      getTransitionProps,
      portalRef,
      isTopModal,
      exited,
      hasTransition,
    } = useModal({
      ...propsWithDefaults,
      rootRef: forwardedRef,
    });
  
    const classes = {
      hidden: !open && exited,
    };
  
    const childProps = {};
    if (children.props.tabIndex === undefined) {
      childProps.tabIndex = '-1';
    }
  
    // It's a Transition like component
    if (hasTransition) {
      const { onEnter, onExited } = getTransitionProps();
      childProps.onEnter = onEnter;
      childProps.onExited = onExited;
    }
  
    const rootProps = {
      ...other,
      className: clsx(classes),
      ...getRootProps(other),
    };
  
    const backdropProps = {
      open,
      ...getBackdropProps(),
    };
  
    if (!keepMounted && !open && (!hasTransition || exited)) {
      return null;
    }
  
    return (
      <Portal ref={portalRef} container={container} disablePortal={disablePortal}>
        {/*
         * Marking an element with the role presentation indicates to assistive technology
         * that this element should be ignored; it exists to support the web application and
         * is not meant for humans to interact with directly.
         * https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md
         */}
        <CustomModalRoot {...rootProps}>
          {!hideBackdrop ? <CustomModalBackdrop {...backdropProps} /> : null}
          <FocusTrap
            disableEnforceFocus={disableEnforceFocus}
            disableAutoFocus={disableAutoFocus}
            disableRestoreFocus={disableRestoreFocus}
            isEnabled={isTopModal}
            open={open}
          >
            {React.cloneElement(children, childProps)}
          </FocusTrap>
        </CustomModalRoot>
      </Portal>
    );
  });
  
Modal.propTypes = {
    children: PropTypes.element.isRequired,
    closeAfterTransition: PropTypes.bool,
    container: PropTypes.oneOfType([
      function (props, propName) {
        if (props[propName] == null) {
          return new Error("Prop '" + propName + "' is required but wasn't specified");
        } else if (
          typeof props[propName] !== 'object' ||
          props[propName].nodeType !== 1
        ) {
          return new Error("Expected prop '" + propName + "' to be of type Element");
        }
      },
      PropTypes.func,
    ]),
    disableAutoFocus: PropTypes.bool,
    disableEnforceFocus: PropTypes.bool,
    disableEscapeKeyDown: PropTypes.bool,
    disablePortal: PropTypes.bool,
    disableRestoreFocus: PropTypes.bool,
    disableScrollLock: PropTypes.bool,
    hideBackdrop: PropTypes.bool,
    keepMounted: PropTypes.bool,
    onClose: PropTypes.func,
    onTransitionEnter: PropTypes.func,
    onTransitionExited: PropTypes.func,
    open: PropTypes.bool.isRequired,
  };
  
  export const Backdrop = React.forwardRef((props, ref) => {
    const { open, ...other } = props;
    return (
      <Fade in={open}>
        <div ref={ref} {...other} />
      </Fade>
    );
  });
  
  Backdrop.propTypes = {
    open: PropTypes.bool,
  };
  
  export const blue = {
    200: '#99CCFF',
    300: '#66B2FF',
    400: '#3399FF',
    500: '#007FFF',
    600: '#0072E5',
    700: '#0066CC',
  };
  
  export const grey = {
    50: '#F3F6F9',
    100: '#E5EAF2',
    200: '#DAE2ED',
    300: '#C7D0DD',
    400: '#B0B8C4',
    500: '#9DA8B7',
    600: '#6B7A90',
    700: '#434D5B',
    800: '#303740',
    900: '#1C2025',
  };
  
  export const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
  };
  
  export const ModalContent = styled('div')(
    ({ theme }) => css`
      font-family: 'IBM Plex Sans', sans-serif;
      font-weight: 500;
      text-align: start;
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;
      overflow: hidden;
      background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
      border-radius: 8px;
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
      box-shadow: 0 4px 12px
        ${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
      padding: 24px;
      color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};
  
      & .modal-title {
        margin: 0;
        line-height: 1.5rem;
        margin-bottom: 8px;
      }
  
      & .modal-description {
        margin: 0;
        line-height: 1.5rem;
        font-weight: 400;
        color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
        margin-bottom: 4px;
      }
    `,
  );
  
  const CustomModalRoot = styled('div')`
    position: fixed;
    z-index: 1300;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  
  const CustomModalBackdrop = styled(Backdrop)`
    z-index: -1;
    position: fixed;
    inset: 0;
    background-color: rgb(0 0 0 / 0.5);
    -webkit-tap-highlight-color: transparent;
  `;


  
  